import {
  faArrowPointer,
  faEye,
  faHourglassHalf,
  faInfinity,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssistChip, Stack, Text } from '@sixui/ui';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { ITableRowProps } from '~/components/molecules/Table';
import type { IAd } from '~/lib/api/modules/Ads';
import { Duration } from '~/components/atoms/Duration';
import { Empty } from '~/components/atoms/Empty';
import { Link } from '~/components/atoms/Link';
import { RelativeTime } from '~/components/atoms/RelativeTime';
import { Thumbnail } from '~/components/atoms/Thumbnail';
import { Table } from '~/components/molecules/Table';
import { usePictureQuery } from '~/lib/api/modules/Pictures/hooks/usePictureQuery';
import { useVideoQuery } from '~/lib/api/modules/Videos/hooks/useVideoQuery';
import { AdListItemMenu } from './AdListItemMenu';

export type IAdListItemProps = ITableRowProps & {
  ad: IAd;
};

export const AdListItem: React.FC<IAdListItemProps> = (props) => {
  const { ad, ...other } = props;
  const { t, i18n } = useTranslation();

  const { appId, media: mediaId, mediaType } = ad;
  const pictureId = mediaType === 'picture' ? mediaId : undefined;
  const videoId = mediaType === 'video' ? mediaId : undefined;

  // TODO: avoid making this request, see tickets APIV1-3 and APIV1-5.
  const pictureQuery = usePictureQuery(
    pictureId ? { appId, pictureId } : undefined,
  );
  const videoQuery = useVideoQuery(videoId ? { appId, videoId } : undefined);

  const actionUrl = `/apps/${ad.appId}/ads/${ad._id}/update`;
  const mediaLoading = pictureQuery.isLoading || videoQuery.isLoading;
  const thumbnailUrl = pictureQuery.data?.thumbUrl ?? videoQuery.data?.thumbUrl;
  const displayCount = ad.limits?.maxDisplays
    ? ad.limits.maxDisplays - (ad.remaining?.displays ?? 0)
    : undefined;
  const clickCount = ad.limits?.maxClicks
    ? ad.limits.maxClicks - (ad.remaining?.clicks ?? 0)
    : undefined;
  const locale = i18n.language;
  const startAt = ad.limits?.notBefore && DateTime.fromISO(ad.limits.notBefore);
  const endAt = ad.limits?.notAfter && DateTime.fromISO(ad.limits.notAfter);
  const ended = endAt ? endAt < DateTime.now() : false;
  const started = startAt ? startAt < DateTime.now() : true;
  const running = started && !ended;
  const programmedAt = !running && !ended ? startAt : undefined;
  const start = startAt
    ? startAt.toLocaleString(DateTime.DATE_MED, { locale })
    : undefined;
  const end = endAt
    ? endAt.toLocaleString(DateTime.DATE_MED, { locale })
    : undefined;

  return (
    <Table.Row {...other}>
      <Stack as={Table.BodyAction} horizontal gap={3} href={actionUrl}>
        <Thumbnail url={thumbnailUrl} loading={mediaLoading} size="lg" />

        <Stack gap={1}>
          {(ad.campaignId || start || end) && (
            <Stack>
              {ad.campaignId && (
                <Text variant="label" size="lg">
                  {ad.campaignId}
                </Text>
              )}

              {(start || end) && (
                <Text variant="body" size="md">
                  {start && end
                    ? t('common.dateRange', { start, end })
                    : start
                      ? t('common.fromDate', { start })
                      : end
                        ? t('common.untilDate', { end })
                        : undefined}
                </Text>
              )}
            </Stack>
          )}

          <Stack horizontal gap={2}>
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faEye} fixedWidth />
              <Text truncateLines={1}>
                {ad.limits?.maxDisplays ? (
                  <>
                    {displayCount ?? 0} / {ad.limits.maxDisplays}
                  </>
                ) : (
                  <FontAwesomeIcon icon={faInfinity} />
                )}
              </Text>
            </Stack>

            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faArrowPointer} fixedWidth />
              <Text truncateLines={1}>
                {ad.limits?.maxClicks ? (
                  <>
                    {clickCount ?? 0} / {ad.limits.maxClicks}
                  </>
                ) : (
                  <FontAwesomeIcon icon={faInfinity} />
                )}
              </Text>
            </Stack>

            {running && endAt && (
              <Stack horizontal gap={1}>
                <FontAwesomeIcon icon={faHourglassHalf} fixedWidth />
                <Text truncate>
                  <Duration
                    ms={endAt.diffNow().as('milliseconds')}
                    human={{ minUnit: 'hours', unitDisplay: 'short' }}
                  />
                </Text>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Stack as={Table.Body} gap={1}>
        <div>
          {ad.url ? (
            <Link to={ad.url} target="_blank">
              {ad.url}
            </Link>
          ) : (
            <Empty />
          )}
        </div>
      </Stack>

      <Stack as={Table.Body} gap={1}>
        <div>
          <AssistChip
            label={
              ended
                ? t('AdListItem.ended')
                : ad.active
                  ? programmedAt
                    ? t('AdListItem.programmed')
                    : running
                      ? t('AdListItem.running')
                      : undefined
                  : running
                    ? t('AdListItem.suspended')
                    : t('AdListItem.disabled')
            }
          />
        </div>
        {programmedAt && (
          <Text variant="body" size="sm" dimmed>
            <RelativeTime date={programmedAt} />
          </Text>
        )}
      </Stack>

      <Stack as={Table.Body} horizontal justify="end">
        <AdListItemMenu ad={ad} />
      </Stack>
    </Table.Row>
  );
};
